import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'welcome',
    category: 'getting-started',
    title: 'Vítejte — jak platforma funguje',
    summary: 'Rychlý přehled toho, co platforma umí, a kroků potřebných k získání výsledku.',
    body: (
      <>
        <p>
          Platforma EcoEmpower vám pomáhá plánovat a řídit energii vaší komunity —
          předpovídá, kolik energie budovy spotřebují, optimalizuje, kdy ji využívat, a
          odhaduje náklady a přínosy investic, jako jsou solární panely nebo baterie.
        </p>
        <p>Práce s platformou se řídí pěti jednoduchými kroky:</p>
        <ol>
          <li><strong>Vytvořte komunitu</strong> — zadejte název, zemi a měnu.</li>
          <li><strong>Přidejte budovy a aktiva</strong> — budovy ve vaší komunitě a veškeré
            zařízení, jako jsou solární panely, baterie nebo nabíječky pro EV.</li>
          <li><strong>Nahrajte data</strong> — historické energetické soubory (soubory CSV) popisující,
            kolik energie každá budova spotřebovává, kolik vaše solární/větrné zdroje vyrábějí a jaké jsou ceny elektřiny.</li>
          <li><strong>Spusťte nástroje</strong> — Prognózování, Optimalizace a Dlouhodobé plánování.</li>
          <li><strong>Zobrazte výsledky</strong> — grafy a čísla, která lze uložit a exportovat.</li>
        </ol>
        <Tip>
          Nástroje fungují nejlépe s reálnými daty, ale platformu si můžete prozkoumat ještě předtím, než je budete mít —
          většina nástrojů v takovém případě zobrazí ilustrativní vzorové výsledky, takže nic není zablokováno.
        </Tip>
        <ScreenshotPlaceholder
          img="dashboard-overview.png"
          description="The main Dashboard after logging in, showing the community count cards and the three action cards (Communities, Quick Start, Documentation)."
          source="Platform — log in and capture the main Dashboard (the page at the root URL, e.g. http://localhost/)."
          caption="Přehled je vaším výchozím bodem."
        />
        <h5 className="mt-4">Kde data hrají roli</h5>
        <p>
          Nejdůležitějším — a nejčastějším — úkolem je správné nahrání datových souborů.
          Část průvodce <strong>Příprava a nahrávání dat</strong> vysvětluje přesně,
          jak to udělat, včetně šablon připravených k použití. Pokud je nahrávání někdy odmítnuto,
          část <strong>Řešení problémů</strong> vysvětluje každou chybovou zprávu srozumitelným jazykem.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-overview',
    category: 'data',
    title: 'Jaké datové soubory potřebuji?',
    summary: 'Úplný seznam datových souborů, k čemu každý slouží a kdy ho potřebujete.',
    body: (
      <>
        <p>
          Data se nahrávají jako <strong>soubory CSV</strong> — jednoduché tabulkové soubory, které
          lze vytvořit v aplikaci Excel nebo Google Sheets. Existují dvě skupiny:
        </p>
        <ul>
          <li><strong>Data budov</strong> — nahrávaná pro konkrétní budovu (její spotřeba energie, její střešní solár, její účty).</li>
          <li><strong>Data komunity</strong> — nahrávaná jednou pro celou komunitu (sdílená solární elektrárna, větrná turbína, ceny elektřiny, počasí).</li>
        </ul>

        <h5 className="mt-4">Datové soubory budov</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Soubor</th><th>Sloupce (povinné)</th><th>K čemu slouží</th></tr>
          </thead>
          <tbody>
            <tr><td>Profil zatížení budovy</td><td><Code>timestamp</Code>, <Code>consumption_kwh</Code></td><td>Kolik elektřiny budova každou hodinu spotřebuje — potřebné pro prognózování a optimalizaci.</td></tr>
            <tr><td>Výroba FV budovy</td><td><Code>timestamp</Code>, <Code>generation_kwh</Code></td><td>Kolik elektřiny vyprodukoval vlastní střešní solár budovy za každou hodinu.</td></tr>
            <tr><td>Účet za energii budovy</td><td><Code>period_start</Code>, <Code>period_end</Code>, <Code>consumption_kwh</Code>, <Code>cost_eur</Code></td><td>Měsíční účty — používají se pro analýzu nákladů.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Datové soubory komunity</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Soubor</th><th>Sloupce (povinné)</th><th>K čemu slouží</th></tr>
          </thead>
          <tbody>
            <tr><td>Profil výroby FV</td><td><Code>timestamp</Code>, <Code>generation_kw</Code></td><td>Výstup sdílené solární elektrárny komunity.</td></tr>
            <tr><td>Profil větrné výroby</td><td><Code>timestamp</Code>, <Code>generation_kw</Code></td><td>Výstup větrné turbíny komunity.</td></tr>
            <tr><td>Historický tarif odběru</td><td><Code>timestamp</Code>, <Code>import_price_eur_kwh</Code></td><td>Cena, kterou jste platili za nákup elektřiny, hodinu po hodině, za celý rok.</td></tr>
            <tr><td>Historický tarif dodávky</td><td><Code>timestamp</Code>, <Code>export_price_eur_kwh</Code></td><td>Cena, kterou jste dostávali za prodej elektřiny zpět do sítě.</td></tr>
            <tr><td>Day-Ahead tarif odběru / dodávky</td><td>stejné jako výše</td><td>Hodinové ceny na zítřek (24 řádků) — nahráváno každý den pro optimalizaci Day-Ahead.</td></tr>
            <tr><td>Solární počasí</td><td><Code>timestamp</Code>, <Code>ghi_w_m2</Code></td><td>Sluneční záření a teplota — zlepšuje solární prognózy.</td></tr>
            <tr><td>Větrné počasí</td><td><Code>timestamp</Code>, <Code>wind_speed_m_s</Code></td><td>Rychlost větru — zlepšuje větrné prognózy.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Odkud tato data pochází?</h5>
        <p>Tato data obvykle nezadáváte ručně — exportujete je ze systému, který již používáte:</p>
        <ul>
          <li><strong>Spotřeba budovy</strong> — z online portálu chytrého elektroměru budovy,
            od vašeho provozovatele distribuční soustavy (DSO) nebo z zákaznického účtu u vašeho dodavatele elektřiny.</li>
          <li><strong>Výroba solárních / větrných zdrojů</strong> — z monitorovací aplikace nebo webového portálu
            střídače nebo turbíny (obvykle nastaveno instalatérem).</li>
          <li><strong>Tarify</strong> — z vaší smlouvy o dodávce elektřiny nebo od dodavatele; ceny Day-Ahead
            pocházejí z národní burzy s elektřinou nebo opět od vašeho dodavatele.</li>
          <li><strong>Počasí</strong> — od národní meteorologické služby nebo bezplatného poskytovatele
            meteorologických dat. Tento soubor je volitelný.</li>
        </ul>
        <p>
          Tyto systémy vám obvykle umožňují exportovat zvolený časový rozsah jako soubor CSV nebo Excel. Jakmile
          takový soubor máte, následující dva články vysvětlují, jak ho uvést do správného formátu.
        </p>

        <Tip>
          Pro začátek <strong>nepotřebujete</strong> všechny soubory. Základem je
          <strong> Profil zatížení budovy</strong> pro každou budovu a soubor s <strong>tarifem</strong>.
          Soubory výroby a počasí přidejte později pro zvýšení přesnosti.
        </Tip>
        <p className="mt-3">
          Každý typ souboru má v této části vlastní krátký článek s příkladem a šablonou ke stažení.
          Začněte článkem <em>„Správné vytváření souborů CSV"</em> níže — předchází velké většině problémů s nahráváním.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-csv-basics',
    category: 'data',
    title: 'Správné vytváření souborů CSV (Excel a Google Sheets)',
    summary: 'Zlatá pravidla pro platný soubor — a nejčastější chyba, které se vyvarovat.',
    body: (
      <>
        <p>
          <strong>Soubor CSV</strong> je jen tabulka uložená v jednoduchém textovém formátu.
          Připravíte ho v aplikaci Excel nebo Google Sheets jako běžnou tabulku a pak ho uložíte jako CSV.
          Dodržujte tato pravidla a vaše nahrávání bude fungovat napoprvé.
        </p>

        <h5 className="mt-4">Zlatá pravidla</h5>
        <ol>
          <li><strong>První řádek = záhlaví sloupců.</strong> První řádek musí obsahovat přesné
            názvy sloupců pro daný typ souboru (například <Code>timestamp</Code> a <Code>consumption_kwh</Code>).
            Pravopis a malá písmena musí přesně odpovídat.</li>
          <li><strong>Jeden řádek na hodinu.</strong> Každý následující řádek představuje jednu hodinu dat. Celý
            rok tvoří <strong>8 760 řádků</strong> (365 dní × 24 hodin) plus řádek záhlaví.</li>
          <li><strong>Použijte formát časového razítka</strong> <Code>2025-01-01T14:00:00Z</Code> — tedy
            Rok-Měsíc-Den, písmeno <Code>T</Code>, pak Hodina:Minuta:Sekunda, pak písmeno <Code>Z</Code>.</li>
          <li><strong>Čísla se oddělují tečkou, nikdy čárkou.</strong> Pište <Code>3.5</Code>, nikoli <Code>3,5</Code>.</li>
          <li><strong>Žádné prázdné řádky, součty ani poznámky</strong> — pouze řádek záhlaví a datové řádky.</li>
        </ol>

        <Warn>
          <strong>Nejčastější chyba.</strong> V Itálii, Francii, Německu, Česku a Řecku
          tabulkové programy běžně zapisují desetinná čísla s <strong>čárkou</strong>
          (<Code>0,457</Code>). Platforma očekává <strong>tečku</strong> (<Code>0.457</Code>), takže
          soubor s čárkami je odmítnut s chybou „Value must be a number". Pokud váš soubor již čárky obsahuje,
          <strong>nemusíte</strong> ho přepisovat — viz{' '}
          <ArticleLink to="data-fix-commas">Oprava souboru, který používá čárky jako desetinný oddělovač</ArticleLink>.
        </Warn>

        <h5 className="mt-4">O sloupci s časovým razítkem</h5>
        <p>
          Nejspolehlivější formát je <Code>2025-01-01T14:00:00Z</Code> a má skrytou
          výhodu: Excel má tendenci tiše přeformátovat vše, co rozpozná jako datum, ale
          tento tvar obvykle ponechá beze změny díky přítomnosti <Code>T</Code> a <Code>Z</Code>. Platforma
          také rozumí běžným evropským formátům, jako je <Code>01/01/2025 14:00</Code>
          a <Code>2025-01-01</Code> — ale právě ty Excel pravděpodobně změní bez vašeho vědomí.
        </p>
        <p>
          Pokud byla vaše časová razítka poškozena nebo potřebujete sloupec sestavit, vložte tento vzorec do
          volného sloupce (zde je původní datum/čas v buňce <Code>A2</Code>):
        </p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`=TEXT(A2,"yyyy-mm-ddThh:mm:ss")&"Z"`}
        </pre>
        <p>
          Zkopírujte ho do všech řádků, pak vyberte sloupec a použijte
          <strong> Kopírovat → Vložit jinak → Hodnoty</strong> pro zafixování výsledku. Tento sloupec použijte jako
          <Code>timestamp</Code>.
        </p>

        <h5 className="mt-4">Kolik řádků? (rozlišení dat)</h5>
        <p>
          Jeden řádek na časový interval. <strong>Hodinová</strong> data jsou typická — celý rok je
          <strong> 8 760 řádků</strong> — ale akceptována jsou také data v <strong>15minutových</strong> a <strong>denních</strong>
          intervalech. Je potřeba minimálně 24 řádků (jeden den).
        </p>

        <h5 className="mt-4">Uložení jako CSV z aplikace Microsoft Excel</h5>
        <ol>
          <li>Klikněte na <strong>Soubor → Uložit jako</strong>.</li>
          <li>V rozevíracím seznamu typů souborů zvolte <strong>„CSV UTF-8 (oddělený čárkami) (*.csv)"</strong>.</li>
          <li>Klikněte na <strong>Uložit</strong>. Pokud Excel upozorní, že některé funkce budou ztraceny, klikněte na <strong>Ano</strong> — to se očekává.</li>
        </ol>
        <p className="small text-muted">
          Pokud vaše verze aplikace Excel nenabízí „CSV UTF-8", zvolte místo toho „CSV (oddělený čárkami)".
        </p>
        <ScreenshotPlaceholder
          img="excel-save-as-csv.png"
          description="Excel's Save As dialog with the file-type dropdown open, highlighting the 'CSV UTF-8 (Comma delimited)' option."
          source="Microsoft Excel — open any workbook → File → Save As → click the file-type dropdown. Capture the dropdown open with the CSV UTF-8 entry highlighted."
          caption={'V aplikaci Excel vždy zvolte „CSV UTF-8".'}
        />

        <h5 className="mt-4">Uložení jako CSV z aplikace Google Sheets</h5>
        <ol>
          <li>Klikněte na <strong>Soubor → Stáhnout → Hodnoty oddělené čárkami (.csv)</strong>.</li>
          <li>Soubor se uloží do složky Stažené soubory ve vašem počítači.</li>
        </ol>
        <ScreenshotPlaceholder
          img="gsheets-download-csv.png"
          description="Google Sheets File → Download submenu, highlighting 'Comma-separated values (.csv)'."
          source="Google Sheets (sheets.google.com) — open any spreadsheet → File → Download. Capture the submenu open with 'Comma-separated values (.csv)' highlighted."
          caption="V Google Sheets použijte Soubor → Stáhnout → .csv."
        />

        <h5 className="mt-4">Zkontrolujte soubor před nahráním</h5>
        <p>
          Uložený soubor <Code>.csv</Code> ještě jednou otevřete a ověřte níže uvedené body. Chcete-li mít
          úplnou jistotu, otevřete ho v prostém textovém editoru (<strong>Notepad</strong> ve Windows,
          <strong> TextEdit</strong> na Macu) místo v tabulkovém procesoru — textový editor zobrazuje skutečný obsah
          souboru, zatímco Excel znovu aplikuje vaše místní formátování na obrazovce a může skrýt skutečný problém.
        </p>
        <p>Ověřte, že:</p>
        <ul>
          <li>První řádek zobrazuje správné názvy sloupců.</li>
          <li>Čísla vypadají jako <Code>3.5</Code> (tečka), nikoli <Code>3,5</Code> (čárka).</li>
          <li>Časová razítka vypadají jako <Code>2025-01-01T14:00:00Z</Code>.</li>
          <li>Na konci nejsou žádné prázdné řádky ani souhrnné řádky.</li>
        </ul>
        <Tip>
          Nejjednodušší způsob: stáhněte šablonu z některého článku věnovaného konkrétnímu typu souboru v této části,
          nahraďte vzorová čísla vlastními a uložte. Šablona již obsahuje správná záhlaví a formát čísel.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-fix-commas',
    category: 'data',
    title: 'Oprava souboru, který používá čárky jako desetinný oddělovač',
    summary: 'Převeďte existující soubor s čárkovými čísly (3,5) do formátu s tečkou, který platforma vyžaduje (3.5) — bez nutnosti cokoliv přepisovat.',
    body: (
      <>
        <p>
          Tabulkové programy v Itálii, Francii, Německu, Česku a Řecku zapisují desetinná
          čísla s <strong>čárkou</strong> — <Code>0,457</Code> místo
          {' '}<Code>0.457</Code>. Platforma potřebuje formát s tečkou. Dobrá zpráva: nemusíte
          {' '}<strong>nic</strong> přepisovat. Stačí změnit jedno nastavení, soubor znovu uložit
          a máte hotovo. Použijte metodu, která odpovídá programu, který máte k dispozici.
        </p>

        <h5 className="mt-3">Metoda 1 — Google Sheets (nejjednodušší, zdarma)</h5>
        <ol>
          <li>Přejděte na <strong>sheets.google.com</strong> a otevřete prázdnou tabulku.</li>
          <li>Zvolte <strong>Soubor → Importovat → Nahrát</strong> a vyberte svůj soubor CSV.</li>
          <li>Zvolte <strong>Soubor → Nastavení</strong>. V části <strong>Národní prostředí</strong> vyberte
            {' '}<strong>Spojené státy</strong> (nebo Spojené království) a klikněte na <strong>Uložit nastavení</strong>.
            Tím sdělíte aplikaci Google Sheets, aby používala tečku jako desetinný oddělovač.</li>
          <li>Zvolte <strong>Soubor → Stáhnout → Hodnoty oddělené čárkami (.csv)</strong>.</li>
        </ol>
        <p>Stažený soubor nyní používá tečky a je připraven k nahrání.</p>
        <ScreenshotPlaceholder
          img="gsheets-locale-setting.png"
          description="Google Sheets — the File → Settings dialog with the Locale dropdown open and 'United States' highlighted."
          source="Google Sheets (sheets.google.com) — open any spreadsheet → File → Settings → the General tab. Capture the modal with the Locale dropdown open and 'United States' highlighted."
          caption="Nastavením národního prostředí na Spojené státy zajistíte, že Google Sheets bude exportovat tečky."
        />

        <h5 className="mt-4">Metoda 2 — Microsoft Excel</h5>
        <ol>
          <li>Otevřete soubor v aplikaci Excel a přejděte na <strong>Soubor → Možnosti → Upřesnit</strong>.</li>
          <li>V části <strong>Možnosti úprav</strong> zrušte zaškrtnutí políčka <strong>„Použít systémové oddělovače"</strong>.</li>
          <li>Nastavte <strong>Oddělovač desetinných míst</strong> na tečku <Code>.</Code> a
            {' '}<strong>Oddělovač tisíců</strong> na mezeru (nebo pole ponechte prázdné).</li>
          <li>Klikněte na <strong>OK</strong>, pak použijte <strong>Soubor → Uložit jako</strong> a zvolte
            {' '}<strong>„CSV UTF-8 (oddělený čárkami)"</strong>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="excel-separator-options.png"
          description="Excel — File → Options → Advanced, the 'Editing options' area, with 'Use system separators' unticked and the decimal/thousands separator boxes visible."
          source="Microsoft Excel — open any workbook → File → Options → Advanced tab → scroll to 'Editing options'. Capture the section with the 'Use system separators' checkbox unticked."
          caption="V aplikaci Excel vypněte systémové oddělovače a nastavte oddělovač desetinných míst na tečku."
        />
        <Tip>Toto nastavení se vztahuje na celou aplikaci Excel. Možnost „Použít systémové oddělovače" můžete poté znovu zapnout.</Tip>

        <h5 className="mt-4">Metoda 3 — Najít a nahradit (krajní řešení)</h5>
        <p>
          Tuto metodu použijte pouze v případě, že jsou čísla uložena jako prostý text a soubor <strong>neobsahuje</strong>
          {' '}oddělovače tisíců (nic ve stylu <Code>1.234,5</Code>), protože slepé nahrazení by poškodilo i ta.
        </p>
        <ol>
          <li>Vyberte pouze sloupce obsahující čísla.</li>
          <li>Otevřete <strong>Najít a nahradit</strong> (Ctrl+H), vyhledejte <Code>,</Code>, nahraďte
            {' '}<Code>.</Code> a klikněte na Nahradit vše.</li>
          <li>Před uložením zkontrolujte několik hodnot.</li>
        </ol>

        <h5 className="mt-4">A co online konverzní nástroje?</h5>
        <p>
          Existují bezplatné webové stránky s „konvertorem CSV", ale ty vyžadují nahrání vašeho souboru na web třetí strany,
          a energetická data mohou být citlivá. Doporučujeme výše uvedené metody — využívají software, který již máte, a
          soubor zůstane ve vašem počítači. Pokud vaše organizace již používá Google Workspace, metoda 1 zůstane v rámci
          nástrojů, kterým důvěřujete.
        </p>

        <Warn>
          Soubor <Code>.csv</Code> otevřený v aplikaci Excel může <em>vypadat</em>, jako by stále obsahoval čárky,
          i když byl uložen s tečkami, protože Excel znovu aplikuje vaše místní formátování na obrazovce. Chcete-li vidět,
          co je v souboru <strong>skutečně</strong> uloženo, otevřete ho v prostém textovém editoru —
          {' '}<strong>Notepad</strong> ve Windows nebo <strong>TextEdit</strong> na Macu. Tam by čísla měla zobrazovat <Code>3.5</Code>.
        </Warn>
      </>
    ),
  },
]
